import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronDown, ArrowUpRight } from "lucide-react";
import { api, fetchTools } from "../lib/api";

// Convert plain text into JSX, auto-linking tool names that appear inside.
// Each tool name is linked at most ONCE per text block to avoid spam.
function linkifyText(text, allTools, currentSlug) {
    if (!text || allTools.length === 0) return text;

    // Sort by name length desc so "AI Blog Writer" matches before "AI Writer"
    const candidates = allTools
        .filter((t) => t.slug !== currentSlug)
        .map((t) => ({ slug: t.slug, name: t.name }))
        .sort((a, b) => b.name.length - a.name.length);

    const used = new Set();
    const segments = [{ type: "text", value: text }];

    for (const tool of candidates) {
        if (used.has(tool.slug)) continue;
        const re = new RegExp(`\\b${escapeRegex(tool.name)}\\b`, "i");

        for (let i = 0; i < segments.length; i++) {
            const seg = segments[i];
            if (seg.type !== "text") continue;
            const m = seg.value.match(re);
            if (!m) continue;

            const start = m.index;
            const end = start + m[0].length;
            const before = seg.value.slice(0, start);
            const matchText = seg.value.slice(start, end);
            const after = seg.value.slice(end);

            const newSegments = [];
            if (before) newSegments.push({ type: "text", value: before });
            newSegments.push({ type: "link", slug: tool.slug, label: matchText });
            if (after) newSegments.push({ type: "text", value: after });

            segments.splice(i, 1, ...newSegments);
            used.add(tool.slug);
            break; // only first match per tool
        }
    }

    return segments.map((s, i) =>
        s.type === "text" ? (
            <React.Fragment key={i}>{s.value}</React.Fragment>
        ) : (
            <Link
                key={i}
                to={`/tool/${s.slug}`}
                className="font-bold text-black underline decoration-2 underline-offset-4 hover:bg-[#FDE047] transition-colors"
            >
                {s.label}
            </Link>
        )
    );
}

function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export default function ToolArticle({ slug, toolName }) {
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openFaq, setOpenFaq] = useState(0);
    const [allTools, setAllTools] = useState([]);
    const [related, setRelated] = useState([]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        setArticle(null);

        // Fetch article + all tools (for linkify) + related tools (for footer)
        Promise.all([
            api.get(`/tools/${slug}/article`).then((r) => r.data),
            fetchTools(),
        ])
            .then(([art, tools]) => {
                if (cancelled) return;
                setArticle(art);
                setAllTools(tools);
                // Find current tool's category, pick 4 related tools from same category
                const current = tools.find((t) => t.slug === slug);
                if (current) {
                    const sameCat = tools
                        .filter((t) => t.category === current.category && t.slug !== slug)
                        .slice(0, 4);
                    setRelated(sameCat);
                }
            })
            .catch(() => {
                if (!cancelled) setError("Could not load article.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    // Memoize the linkifier per text block
    const linkify = useMemo(
        () => (text) => linkifyText(text, allTools, slug),
        [allTools, slug]
    );

    if (loading) {
        return (
            <section className="mt-12 nb-border-4 nb-shadow rounded-3xl bg-white p-6 md:p-10 flex items-center gap-3 text-neutral-600">
                <Loader2 className="w-5 h-5 animate-spin" strokeWidth={2.5} />
                <span className="font-bold uppercase tracking-widest text-xs">
                    Loading article…
                </span>
            </section>
        );
    }

    if (error || !article) return null;

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: (article.faq || []).map((q) => ({
            "@type": "Question",
            name: q.question,
            acceptedAnswer: { "@type": "Answer", text: q.answer },
        })),
    };

    return (
        <article
            data-testid="tool-article"
            className="mt-12 nb-border-4 nb-shadow rounded-3xl bg-white p-6 md:p-10"
        >
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />

            {/* INTRO */}
            <section>
                <div className="text-xs font-bold uppercase tracking-widest text-neutral-600">
                    About
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-2">
                    {toolName} — Free Online Tool
                </h2>
                <p className="mt-4 text-base md:text-lg text-neutral-800 leading-relaxed whitespace-pre-line">
                    {linkify(article.intro)}
                </p>
            </section>

            {/* WHY USE */}
            <section className="mt-10">
                <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">
                    Why use {toolName}?
                </h2>
                <p className="mt-3 text-base text-neutral-800 leading-relaxed whitespace-pre-line">
                    {linkify(article.why_use)}
                </p>
            </section>

            {/* HOW TO USE */}
            <section className="mt-10">
                <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">
                    How to use {toolName}
                </h2>
                <p className="mt-3 text-base text-neutral-800 leading-relaxed whitespace-pre-line">
                    {linkify(article.how_to_use)}
                </p>
            </section>

            {/* BENEFITS */}
            <section className="mt-10">
                <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">
                    Benefits of {toolName}
                </h2>
                <p className="mt-3 text-base text-neutral-800 leading-relaxed whitespace-pre-line">
                    {linkify(article.benefits)}
                </p>
            </section>

            {/* FAQ */}
            {article.faq && article.faq.length > 0 && (
                <section className="mt-10">
                    <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">
                        Frequently Asked Questions
                    </h2>
                    <div className="mt-4 space-y-3">
                        {article.faq.map((q, i) => {
                            const open = openFaq === i;
                            return (
                                <div
                                    key={i}
                                    className="nb-border rounded-2xl bg-neutral-50 overflow-hidden"
                                    data-testid={`faq-item-${i}`}
                                >
                                    <button
                                        type="button"
                                        onClick={() => setOpenFaq(open ? -1 : i)}
                                        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left font-bold hover:bg-[#FDE047] transition-colors"
                                    >
                                        <span>{q.question}</span>
                                        <ChevronDown
                                            className={`w-5 h-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                                            strokeWidth={3}
                                        />
                                    </button>
                                    {open && (
                                        <div className="px-5 pb-4 pt-0 text-base text-neutral-800 leading-relaxed whitespace-pre-line border-t-2 border-black/10">
                                            {linkify(q.answer)}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* RECOMMENDED LINKS — explicit internal linking footer inside the article */}
            {related.length > 0 && (
                <section
                    data-testid="article-recommended"
                    className="mt-12 nb-border rounded-2xl bg-[#FDE047] p-5 md:p-6"
                >
                    <div className="text-xs font-bold uppercase tracking-widest text-black/70">
                        You may also explore
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-black tracking-tight mt-1">
                        Recommended free AI tools
                    </h3>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                        {related.map((t) => (
                            <Link
                                key={t.slug}
                                to={`/tool/${t.slug}`}
                                data-testid={`article-rec-${t.slug}`}
                                className="group nb-border rounded-xl bg-white p-3 flex items-center justify-between gap-2 hover:-translate-y-[2px] hover:shadow-[3px_3px_0px_#0A0A0A] transition-all"
                            >
                                <div className="min-w-0">
                                    <div className="font-display font-extrabold text-base leading-tight truncate">
                                        {t.name}
                                    </div>
                                    <div className="text-xs text-neutral-700 truncate">
                                        {t.description}
                                    </div>
                                </div>
                                <div className="w-7 h-7 shrink-0 nb-border bg-white rounded-full flex items-center justify-center group-hover:bg-black group-hover:text-white transition-colors">
                                    <ArrowUpRight strokeWidth={2.5} className="w-3.5 h-3.5" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </article>
    );
}
